import {
  faCheckSquare,
  faCircle,
  faDotCircle,
  faSquare,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons'
import { SwitchOptionVariant } from './SwitchOptionVariant'
import { PillOptionVariant } from './PillOptionVariant'
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome'
import React, { useRef } from 'react'

import useOption from './useOption'
import { twMerge } from 'tailwind-merge'
import { Button } from '../../../../Button'

export interface OptionProps {
  value: any
  label: string
  group?: string
  description?: string
  selected?: boolean
  variant?: 'select' | 'base' | 'switch-group' | 'pill-group'
  setSelectOpen?: (open: boolean) => void
  onSelect?: (value: any) => void
  disabled?: boolean
  className?: string
  children?: React.ReactNode
  role?: 'checkbox' | 'radio' | 'switch'
  actions?: {
    label: string
    icon: FontAwesomeIconProps['icon']
    onClick: () => void
    keepSelectOpenOnClick?: boolean
  }[]
  nested?: boolean
}

export const Option = ({
  value,
  label,
  setSelectOpen,
  ...props
}: OptionProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { onSelect = () => {} } = props

  useOption(buttonRef, () => handleSelect())

  const handleSelect = () => {
    onSelect(value)
  }

  const globalClasses = twMerge(
    'transition-all duration-150 ease-in-out',
    'focus:text-blue-800 focus:bg-blue-200',
    'hover:text-blue-800 hover:bg-blue-200',
  )

  const baseButtonClasses = twMerge(
    globalClasses,
    'py-1 pl-2 group',
    'hover:text-base-800 hover:bg-indigo-200',
    props.className,
  )

  const buttonClasses = twMerge(
    globalClasses,
    'py-2 pl-2 group hover:bg-blue-100 focus:bg-base-200 hover:text-blue-800 rounded-md',
    'dark:text-white',
  )

  const onClick = (
    e: React.MouseEvent<HTMLLabelElement | HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    if (!props.disabled) onSelect(e)
  }

  if (props?.variant === 'base')
    return (
      <button
        ref={buttonRef}
        onFocus={() => buttonRef?.current?.focus()}
        tabIndex={0}
        aria-label={label}
        disabled={props.disabled}
        className={baseButtonClasses}
        onClick={onSelect}
        {...props}
        type='button'>
        <div className='flex text-left w-full items-center space-x-4 justify-between mr-2'>
          <span className='flex text-left items-center w-full'>
            {props.children ? props.children : label}
            <div>
              {props.actions?.map((a) => (
                <Button
                  variant='link'
                  onClick={(e) => {
                    e.stopPropagation()
                    a.onClick()
                    !a.keepSelectOpenOnClick && setSelectOpen?.(false)
                  }}
                  icon={a.icon}
                  className='py-0'
                  iconPosition='left'>
                  {props.actions?.length === 1 && a.label}
                </Button>
              ))}
            </div>
          </span>
        </div>
      </button>
    )

  if (props.variant === 'switch-group')
    return (
      <SwitchOptionVariant
        ref={buttonRef}
        label={label}
        disabled={props.disabled}
        role={props.role}
        selected={props.selected}
        onClick={onClick}>
        {props.children}
      </SwitchOptionVariant>
    )

  if (props.variant === 'pill-group')
    return (
      <PillOptionVariant
        ref={buttonRef}
        label={label}
        disabled={props.disabled}
        selected={props.selected}
        onClick={onClick}>
        {props.children}
      </PillOptionVariant>
    )

  return (
    <button
      ref={buttonRef}
      onFocus={() => buttonRef?.current?.focus()}
      tabIndex={0}
      aria-label={label}
      disabled={props.disabled}
      className={buttonClasses}
      onClick={onSelect}
      {...props}
      type='button'>
      <div className='flex text-left items-center space-x-4 justify-between mr-2'>
        <span className='flex text-left items-center'>
          {props.selected ? (
            <FontAwesomeIcon
              className={`${'text-blue-600'} h-4! w-4! group-hover:text-white group-focus:text-indigo-600 mr-2`}
              icon={props.role === 'checkbox' ? faCheckSquare : faDotCircle}
            />
          ) : (
            <FontAwesomeIcon
              className={`text-white border border-blue-600 ${
                props.role === 'checkbox' ? '' : 'rounded-full'
              } h-4! w-4! mr-2 `}
              icon={props.role === 'checkbox' ? faSquare : faCircle}
            />
          )}

          {props.children ? props.children : label}
        </span>

        {/* Remove Indicator */}
        <div>
          {props.actions?.map((a) => (
            <Button
              variant='link'
              onClick={(e) => {
                console.log('clicked')
                e.stopPropagation()
                a.onClick()
                !a.keepSelectOpenOnClick && setSelectOpen?.(false)
              }}
              icon={a.icon}
              className='py-0'
              iconPosition='left'>
              {props.actions?.length === 1 && a.label}
            </Button>
          ))}

          {props.selected && (
            <FontAwesomeIcon
              size='sm'
              className={`text-red-200 rounded-full group-hover:text-red-200 group-hover:bg-red-800 group-focus:text-red-200 group-focus:bg-red-800`}
              icon={faTimesCircle}
            />
          )}
        </div>
      </div>
    </button>
  )
}
